import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { withRouter } from 'next/router';
import { WithRouterProps } from 'next/dist/client/with-router';

import cx from 'classnames';
import get from 'lodash/get';
import localForage from 'localforage';

import { postArticleRating } from 'state/actions/articleActions';

import { Button } from 'components/base';
import { LikeIcon, DislikeIcon } from 'styled';

import Language from 'constants/Language';

import { GlobalState, Status } from 'types';

interface PassedProps {
  rating: number;
}

interface StoreProps {
  postArticleRatingStatus: Status;
}

interface DispatchProps {
  actions: {
    postArticleRating(slug: string, isUpvote: boolean): void;
  };
}

type Props = PassedProps & DispatchProps & WithRouterProps & StoreProps;

interface State {
  userSelection: string;
  alreadySubmitted: boolean;
  submittedInCurrentSession: boolean;
}

const LIKE = 'like';
const DISLIKE = 'dislike';

class ArticleRating extends Component<Props, State> {
  state: State = {
    userSelection: '',
    alreadySubmitted: false,
    submittedInCurrentSession: false,
  };

  componentDidMount() {
    const articleSlug = (get(
      this,
      'props.router.query.contentHubSlugs',
      []
    ) as string[]).slice(-1)[0];

    localForage.getItem(`article_rating_${articleSlug}`, (err, value) => {
      if (value === true) {
        this.setState({ userSelection: LIKE, alreadySubmitted: true });
      } else if (value === false) {
        this.setState({ userSelection: DISLIKE, alreadySubmitted: true });
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.postArticleRatingStatus === Status.PENDING &&
      this.props.postArticleRatingStatus === Status.FULFILLED
    ) {
      this.setState({ submittedInCurrentSession: true });
    }
  }

  handleLikeButtonClick = () => {
    const articleSlug = (get(
      this,
      'props.router.query.contentHubSlugs',
      []
    ) as string[]).slice(-1)[0];

    this.props.actions.postArticleRating(`/${articleSlug}`, true);
    this.setState({ userSelection: LIKE });
  };

  handleDislikeButtonClick = () => {
    const articleSlug = (get(
      this,
      'props.router.query.contentHubSlugs',
      []
    ) as string[]).slice(-1)[0];
    this.props.actions.postArticleRating(`/${articleSlug}`, false);
    this.setState({ userSelection: DISLIKE });
  };

  render() {
    const { rating } = this.props;
    const {
      userSelection,
      alreadySubmitted,
      submittedInCurrentSession,
    } = this.state;
    const postArticleRatingStatusHasFulfilled =
      submittedInCurrentSession || alreadySubmitted;
    const ratingResult = submittedInCurrentSession ? rating + 1 : rating;

    return (
      <div className="ArticleRating bg-color-gray-lightest article-content-padding pt2_5 pb2_5 md:pt3">
        <div>
          <span className="block text-md">
            {Language.t('ArticleRating.title')}
          </span>
          <div className="mt2 flex items-center flex-wrap ">
            {!!rating ||
              (submittedInCurrentSession && (
                <span className="block text-secondary-sm mr1 md:mr1_5">{`${ratingResult} ${Language.t(
                  'ArticleRating.person',
                  ratingResult
                )} ${Language.t('ArticleRating.description')}`}</span>
              ))}
            <div>
              <Button
                ariaLabel={Language.t('ArticleRating.likeButtonAriaLabel')}
                className={cx('ArticleRating__like-button', {
                  'opacity-50':
                    postArticleRatingStatusHasFulfilled &&
                    userSelection === DISLIKE,
                })}
                disabled={
                  postArticleRatingStatusHasFulfilled && !!userSelection
                }
                onClick={this.handleLikeButtonClick}
              >
                <LikeIcon />
              </Button>
              <Button
                ariaLabel={Language.t('ArticleRating.dislikeButtonAriaLabel')}
                containerClassName="ml_5 md:ml_75"
                className={cx('ArticleRating__dislike-button', {
                  'opacity-50':
                    postArticleRatingStatusHasFulfilled &&
                    userSelection === LIKE,
                })}
                disabled={
                  postArticleRatingStatusHasFulfilled && !!userSelection
                }
                onClick={this.handleDislikeButtonClick}
              >
                <DislikeIcon />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: GlobalState): StoreProps => ({
  postArticleRatingStatus: state.status.postArticleRatingStatus,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  actions: bindActionCreators(
    {
      postArticleRating,
    },
    dispatch
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ArticleRating));
